import type { FileMetadata, FileWithPreview } from './use-file-upload';

/**
 * Validates a file against size and type constraints
 *
 * Checks:
 * - File size doesn't exceed maxSize
 * - File type/extension matches accept pattern (if accept is not '*')
 *
 * Supported accept patterns:
 * - Extension: ".pdf", ".jpg" (matches file extension)
 * - MIME type: "image/png", "application/pdf" (exact match)
 * - Wildcard: "image/*" (matches all files in that category)
 * - Multiple: ".pdf,.doc,.docx" (comma-separated list)
 *
 * @param file - File or FileMetadata to validate
 * @param maxSize - Maximum file size in bytes
 * @param accept - Accepted file types pattern
 * @returns Error message string if validation fails, null if valid
 */
export const validateFile = (file: File | FileMetadata, maxSize: number, accept: string): string | null => {
  // Check file size (works for both File and FileMetadata)
  if (file.size > maxSize) {
    return `File "${file.name}" exceeds the maximum size of ${formatBytes(maxSize)}.`;
  }

  // If accept is '*', accept all file types
  if (accept !== '*') {
    // Parse accept string into array of accepted types
    // Example: "image/*,.pdf" -> ["image/*", ".pdf"]
    const acceptedTypes = accept.split(',').map((type) => type.trim());

    // Get file type and extension
    const fileType = file instanceof File ? file.type || '' : file.type;
    const fileExtension = `.${file.name.split('.').pop()}`;

    // Check if file matches any accepted type
    const isAccepted = acceptedTypes.some((type) => {
      // Pattern: Extension match (e.g., ".pdf")
      if (type.startsWith('.')) {
        return fileExtension.toLowerCase() === type.toLowerCase();
      }
      // Pattern: Wildcard category (e.g., "image/*")
      if (type.endsWith('/*')) {
        const baseType = type.split('/')[0]; // Extract "image" from "image/*"
        return fileType.startsWith(`${baseType}/`); // Check if file type starts with "image/"
      }
      // Pattern: Exact MIME type match (e.g., "image/png")
      return fileType === type;
    });

    if (!isAccepted) {
      return `File "${file.name}" is not an accepted file type.`;
    }
  }

  return null; // File is valid
};

/**
 * Creates a preview URL for a file
 *
 * For new File objects: Creates an object URL using URL.createObjectURL()
 * For existing FileMetadata: Returns the server-provided URL
 *
 * Note: Object URLs must be revoked using URL.revokeObjectURL() to prevent memory leaks
 *
 * @param file - File or FileMetadata to create preview for
 * @returns Preview URL string, or undefined if preview cannot be created
 */
export const createPreview = (file: File | FileMetadata): string | undefined => {
  if (file instanceof File) {
    // Create object URL for new files (must be revoked later)
    return URL.createObjectURL(file);
  }
  // Use server URL for existing files
  return file.url;
};

/**
 * Generates a unique identifier for a file
 *
 * For new File objects: Creates a unique ID using filename, timestamp, and random string
 * For existing FileMetadata: Uses the provided ID
 *
 * This ID is used to track files in the state array and identify files for removal
 *
 * @param file - File or FileMetadata to generate ID for
 * @returns Unique identifier string
 */
export const generateUniqueId = (file: File | FileMetadata): string => {
  if (file instanceof File) {
    // Generate unique ID: filename-timestamp-randomString
    // Example: "document.pdf-1234567890-abc123"
    return `${file.name}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
  // Use existing ID for FileMetadata
  return file.id;
};

/**
 * Converts FileMetadata array to FileWithPreview array
 *
 * @param initialFiles - Array of FileMetadata to convert
 * @returns Array of FileWithPreview
 */
export const convertMetadataToFileWithPreview = (initialFiles: FileMetadata[]): FileWithPreview[] => {
  return initialFiles.map((file) => ({
    file,
    id: file.id,
    preview: file.url,
    isFile: false, // Initial files are always FileMetadata
  }));
};

/**
 * Checks if a file is a duplicate of an existing file
 *
 * @param file - File to check
 * @param existingFiles - Array of existing files
 * @returns True if file is a duplicate
 */
export const isDuplicateFile = (file: File, existingFiles: FileWithPreview[]): boolean => {
  return existingFiles.some(
    (existingFile) => existingFile.file.name === file.name && existingFile.file.size === file.size
  );
};

/**
 * Validates and processes files for addition
 *
 * @param files - Array of files to process
 * @param options - Validation options
 * @returns Object containing valid files and errors
 */
export const processFilesForAddition = (
  files: File[],
  options: {
    existingFiles: FileWithPreview[];
    maxFiles: number;
    maxSize: number;
    accept: string;
    multiple: boolean;
  }
): {
  validFiles: FileWithPreview[];
  errors: string[];
} => {
  const { existingFiles, maxFiles, maxSize, accept, multiple } = options;
  const validFiles: FileWithPreview[] = [];
  const errors: string[] = [];

  // Check if adding these files would exceed maxFiles (only in multiple mode)
  if (multiple && maxFiles !== Infinity && existingFiles.length + files.length > maxFiles) {
    errors.push(`You can only upload a maximum of ${maxFiles} files.`);
    return { validFiles, errors };
  }

  // Process each file individually
  files.forEach((file) => {
    // Only check for duplicates if multiple files are allowed
    if (multiple && isDuplicateFile(file, existingFiles)) {
      errors.push(`File "${file.name}" is already uploaded.`);
      return; // Skip this file
    }

    // Check file size before other validations (early exit for large files)
    if (file.size > maxSize) {
      errors.push(
        multiple
          ? `Some files exceed the maximum size of ${formatBytes(maxSize)}.`
          : `File exceeds the maximum size of ${formatBytes(maxSize)}.`
      );
      return; // Skip this file
    }

    // Validate file type and other constraints
    const error = validateFile(file, maxSize, accept);
    if (error) {
      errors.push(error);
    } else {
      // File passed all validations - add to valid files list
      validFiles.push({
        file,
        id: generateUniqueId(file), // Generate unique ID for tracking
        preview: createPreview(file), // Create preview URL
        isFile: true, // Track if this is a File instance
      });
    }
  });

  return { validFiles, errors };
};

/**
 * Revokes object URLs for image files to prevent memory leaks
 *
 * @param files - Array of files to clean up
 */
export const revokeImageObjectURLs = (files: FileWithPreview[]): void => {
  files.forEach((file) => {
    if (file.preview && file.file instanceof File && file.file.type.startsWith('image/')) {
      URL.revokeObjectURL(file.preview);
    }
  });
};

/**
 * Helper function to format bytes to human-readable format
 *
 * Converts byte values to appropriate units (Bytes, KB, MB, GB, etc.)
 * Uses binary (1024) base for conversion.
 *
 * @param bytes - Number of bytes to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1.5 MB", "500 KB")
 *
 * @example
 * formatBytes(1024) // "1 KB"
 * formatBytes(1048576) // "1 MB"
 * formatBytes(1536, 1) // "1.5 KB"
 */
export const formatBytes = (bytes: number | null | undefined, decimals = 2): string => {
  if (bytes === 0 || bytes === null || bytes === undefined) return '-';

  const k = 1024; // Binary base (1024 bytes = 1 KB)
  const dm = decimals < 0 ? 0 : decimals; // Ensure non-negative decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']; // Unit labels

  // Calculate which unit to use (logarithmic calculation)
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Convert bytes to appropriate unit and format with specified decimals
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};
